import React from "react";
import axios from "axios";
import { Table } from "react-bootstrap";
import { useEffect, useState, useCallback, useMemo } from "react";
import GLOBALVAR from "../../constants";
import { format, parseISO } from 'date-fns';
import LoadingSpinner from "../../components/misc/LoadingSpinner";
import './css/ListPaginationLoanProcessing.scss'; 
import ViewportChecker from "./ViewportChecker";
import debounce from 'lodash/debounce';


function ListPaginationLoanProcessing() {
    const [loanProcessingHeaders, setLoanProcessingHeaders] = useState([]);
    const [loanProcessingLists, setLoanProcessingLists] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [count, setCount] = useState(0);
    const itemsPerPage = 10;
    const isMobile = ViewportChecker();

    const fetchDataLoanProcessings = useCallback(async (currentPage, searchQuery = "") => {
        setShowSpinner(true);

        try {
            let response;
            if (searchQuery) {
                response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loansearch`, {
                    params: {
                        query: searchQuery,
                        page: currentPage,
                        limit: itemsPerPage
                    }
                });
            } else {
                response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/all?page=${currentPage}&limit=${itemsPerPage}`);
            }

            const appdata = response.data.rows.reduce((acc, item) => {
                acc[item.id] = {
                    items: [
                        { key: 'id', value: item.id },
                        { key: 'fullname', value: `${item.applicant.lastName}, ${item.applicant.firstName} ${item.applicant.middleName}` },
                        { key: 'amountgranted', value: parseFloat(item.amountGranted).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',') },
                        { key: 'dategranted', value: format(parseISO(item.dateGranted), 'MM/dd/yyyy') },
                        { key: 'interestrate', value: item.interestRate },
                        { key: 'loanterm', value: item.loanTerm },
                        { key: 'loanterm', value: item.loanInterval },
                        { key: 'action', value: item.id }
                    ]
                };
                return acc;
            }, {});

            setCount(response.data.count);
            setLoanProcessingLists(appdata);

            const loanProcessinghead = [
                {value: "ID"},
                {value: "Full Name"},
                {value: "Amount Granted"},
                {value: "Date Granted"},
                {value: "Interest Rate"},
                {value: "Loan Term"},
                {value: "Interval"},
                {value: "Action"}
            ];
    
            setLoanProcessingHeaders(loanProcessinghead);
            
            setShowSpinner(false);
            setIsSearching(false);
        } catch (error) {
            console.error('Error submitting form:', error);
            setShowSpinner(false);
            setIsSearching(false);
        }
    }, [itemsPerPage]); // Only depends on itemsPerPage which is constant

    // Debounced search function
    const debouncedSearch = useCallback(
        (query) => {
            setCurrentPage(1);
            fetchDataLoanProcessings(1, query);
        },
        [fetchDataLoanProcessings]
    );

    const debouncedSearchWithDelay = useMemo(
        () => debounce(debouncedSearch, 500),
        [debouncedSearch]
    );

    const handleSearchChange = (event) => {
        const query = event.target.value;
        setSearchQuery(query);
        setIsSearching(true);
        debouncedSearchWithDelay(query);
    };

    const handleSearchSubmit = (event) => {
        event.preventDefault();
        setCurrentPage(1);
        fetchDataLoanProcessings(1, searchQuery);
    };

    const btnPrev = () => {
        setCurrentPage(prevPage => prevPage - 1);
    };

    const btnNext = () => {
        setCurrentPage(prevPage => prevPage + 1);
    };

    const btnPage = (page) => {
        setCurrentPage(page);
    };

    const btnFirst = () => {
        setCurrentPage(1);
    };

    const btnLast = () => {
        const totalPages = Math.ceil(count / itemsPerPage);
        setCurrentPage(totalPages);
    };

    const totalPages = Math.ceil(count / itemsPerPage);

    const getPageNumbers = () => {
        const pageNumbers = [];
        const totalPagesToShow = 3;

        if (totalPages <= totalPagesToShow) {
            for (let i = 1; i <= totalPages; i++) {
                pageNumbers.push(i);
            }
        } else {
            const lastPage = currentPage + 1 > totalPages ? totalPages : currentPage + 1;
            const firstPage = lastPage - (totalPagesToShow - 1) > 0 ? lastPage - (totalPagesToShow - 1) : 1;
            const dots = currentPage + 1 < totalPages ? '...' : '';

            for (let i = firstPage; i <= lastPage; i++) {
                pageNumbers.push(i);
            }

            if (dots) {
                pageNumbers.push(dots);
            }

            pageNumbers.push(totalPages);
        }

        return pageNumbers;
    };

    const focussearch = () => {
        const inputElement = document.querySelector('.search-input-loan');
        inputElement.focus();
    };

    useEffect(() => {
        fetchDataLoanProcessings(currentPage, searchQuery);
    }, [currentPage, searchQuery, fetchDataLoanProcessings]); // Added fetchDataLoanProcessings to dependencies


    return (
        <>
            {showSpinner && (<LoadingSpinner />)}
            <div className="list-wrapper">
                <div className="list-header">
                    <div className="list-title">
                        {!isMobile && <h1>Processed Loans List</h1>}
                        <div className="search-wrapper">
                            <form onSubmit={handleSearchSubmit} className="searchform" onClick={focussearch}>
                                <div className="search-field">
                                    <div className="search-icon">
                                        <i className="fa-solid fa-magnifying-glass"></i>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Search loans..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        className={`search-input-loan ${isSearching ? 'searching' : ''}`}
                                    />
                                    {searchQuery && (
                                        <button 
                                            type="button" 
                                            className="search-clear" 
                                            onClick={() => {
                                                setSearchQuery('');
                                                setIsSearching(false);
                                                debouncedSearch('');
                                            }}
                                        >
                                            <i className="fa-solid fa-xmark"></i>
                                        </button>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>

                <div className="table-responsive">
                    <Table className="list-container list-loans" hover>
                        <thead>
                            <tr>
                                {loanProcessingHeaders.map((item, index) => (
                                    <th className="list-header-item" key={index}>
                                        {item.value}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.values(loanProcessingLists).reverse().map((item, index) => (
                                <tr className="list-body" key={index}>
                                    {item.items.map((subitem, subindex) => {
                                        let itemval = subitem.value;

                                        if (subitem.key === "id") {
                                            itemval = (
                                                <a href={`/loans/${subitem.value}`} className="id-link">
                                                    {subitem.value.toString().padStart(6, '0')}
                                                </a>
                                            );
                                        }

                                        if (subitem.key === "action") {
                                            itemval = (
                                                <div className="action-buttons">
                                                    <a 
                                                        className="btn-action view" 
                                                        href={`/loans/${subitem.value}`}
                                                        title="View Details"
                                                    >
                                                        <i className="fa-solid fa-magnifying-glass"></i>
                                                    </a>
                                                    <a 
                                                        className="btn-action add" 
                                                        href={`/add-repayment/?id=${subitem.value}`}
                                                        title="Add Payment"
                                                    >
                                                        <i className="fa-solid fa-credit-card"></i>
                                                    </a>
                                                </div>
                                            );
                                        }

                                        return (
                                            <td className="list-body-item" key={`${index}+${subindex}`}>
                                                {itemval}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr className="page-buttons">
                                <td colSpan="8">
                                    <div className="pagination-wrapper">
                                        <div className="page-info">
                                            Showing {loanProcessingLists.length} of {count} entries
                                        </div>
                                        <div className="page-buttons__container">
                                            <button onClick={btnFirst} disabled={currentPage === 1}>
                                                <i className="fa-solid fa-angles-left"></i>
                                            </button>
                                            <button onClick={btnPrev} disabled={currentPage === 1}>
                                                <i className="fa-solid fa-angle-left"></i>
                                            </button>
                                            {getPageNumbers().map((page, index) => (
                                                <React.Fragment key={index}>
                                                    {page === '...' ? (
                                                        <span className="elip">
                                                            <i className="fa-solid fa-ellipsis"></i>
                                                        </span>
                                                    ) : (
                                                        <span className="number">
                                                            <button onClick={() => btnPage(page)} disabled={currentPage === page}>
                                                                {page}
                                                            </button>
                                                        </span>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            <button onClick={btnNext} disabled={currentPage === totalPages}>
                                                <i className="fa-solid fa-angle-right"></i>
                                            </button>
                                            <button onClick={btnLast} disabled={currentPage === totalPages}>
                                                <i className="fa-solid fa-angles-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </td>
                            </tr>
                        </tfoot>
                    </Table>
                </div>
            </div>
        </>
    );
}

export default ListPaginationLoanProcessing;